import React, { useContext, useReducer, useRef, useEffect, useCallback, useState } from 'react';
import {
	GamesContext,
	gamesInitialState,
	IFilterValues,
	TGamesCountOfItems,
	TGamesSortBy,
	TTypeOfModalWindow,
} from './gamesContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { gamesReducer } from './gamesReducer';
import { GamesActionTypes } from './gamesActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedGames, deleteGame } from 'backend/games';
import { AxiosResponse } from 'axios';
import { IGames, IGetPaginatedGames } from 'types';

const GamesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(gamesReducer, gamesInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IGames>, IGetPaginatedGames>(
		isMounted.current ? getPaginatedGames : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			developerId: state.selectedDeveloper,
			categoryId: state.selectedCategory,
			isUnassignedGames: state.isUnassignedGames,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByApplicationId:
				state.sortBy === 'ApplicationIdA-Z' || state.sortBy === 'ApplicationIdZ-A' ? state.sortBy : null,
			sortByVersionCode:
				state.sortBy === 'VersionCode1-10' || state.sortBy === 'VersionCode10-1' ? state.sortBy : null,
			sortByVersionName:
				state.sortBy === 'VersionName1-10' || state.sortBy === 'VersionName10-1' ? state.sortBy : null,
			sortByCategories:
				state.sortBy === 'CategoriesA-Z' || state.sortBy === 'CategoriesZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: GamesActionTypes.SET_GAMES,
				payload: {
					games: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteGame(state.IDForDeletion);
			const filteredGames = state.games.games.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: GamesActionTypes.SET_GAMES,
				payload: {
					games: {
						gamesCount: state.games.gamesCount - 1,
						games: filteredGames,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: GamesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.games]);

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: GamesActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setDeveloper = (value: Nullable<string>) => {
		dispatch({
			type: GamesActionTypes.SET_DEVELOPER,
			payload: {
				selectedDeveloper: value,
			},
		});
	};

	const setCategory = (value: Nullable<string>) => {
		dispatch({
			type: GamesActionTypes.SET_CATEGORY,
			payload: {
				selectedCategory: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: GamesActionTypes.RESET_SELECTED_CONTROLS });

	const toggleUnassignedGames = () => {
		dispatch({ type: GamesActionTypes.TOGGLE_UNASSIGNED_GAMES });
	};

	const setSortBy = (sortBy: TGamesSortBy) => {
		dispatch({
			type: GamesActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TGamesCountOfItems) => {
		dispatch({
			type: GamesActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: GamesActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: GamesActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: GamesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: GamesActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<GamesContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setDeveloper,
				setCategory,
				resetSelectedControls,
				toggleUnassignedGames,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				games: state.games,
				searchString: state.searchString,
				selectedDeveloper: state.selectedDeveloper,
				selectedCategory: state.selectedCategory,
				isUnassignedGames: state.isUnassignedGames,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</GamesContext.Provider>
	);
};

export default GamesState;
